import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { EMPTY, from, of, Observable, forkJoin, combineLatest } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
  take,
  tap,
  concatMap,
  toArray,
} from 'rxjs/operators';
import * as ReportsActions from './reports.actions';
import {
  CaseTopology,
  MethodTypeTopology,
  SampleTopology,
  AnalysisStateTopology,
  ProtocolPipelineTopology,
  PipelineTopology,
  ObjectTopology,
  PipelineStepTopology,
  LabelTopology,
  AnalysisTopology,
  FindingTopology,
  CaseTypeTopology,
  AssetTypeTopology,
  AssetTopology,
  DeviceTopology,
  DeviceTypeTopology,
  UserTopology,
} from '@telespot/infrastructure/parse';
import Parse from 'parse';
import { ILabel, ISample, IAnalyst, ISampleAnalysis, ISampleAnalysisInfo, ISampleCountEntity } from './reports.state';
import {
  getPipelinesForSample,
  selectAnalysts,
  getMethodTypeFromSample,
  getLabelsByMethodTypeId,
  getSampleCountersForAnalyst,
  sampleCropsFromAnalyst,
  loading as selectLoading,
} from './reports.selectors';
import { Label, StepAction, StepTask } from '@telespot/domain';
import { ReportMapper } from '../services/report-generator/report-generator.mapper';
import { User, CloudFunctions, Sample, Algorithms, Finding } from '@telespot/sdk';
import { AuthService, FeaturesService, MosaicService } from '@telespot/web-core';
import { TranslateService } from '@ngx-translate/core';
import { COUNTER_EXCLUDED_STORAGE_KEY_PREFIX, joinStorageKey } from '@analysis/ui';
import { ANALYSED_STATES, isEmptyAnalystScope, ReportAnalystScope, reportAnalystScope } from './analyst-scope';

function readCounterExcludedLabelIdsFromStorage(methodTypeId: string): Set<string> {
  try {
    const raw = localStorage.getItem(joinStorageKey(COUNTER_EXCLUDED_STORAGE_KEY_PREFIX, methodTypeId));
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (Array.isArray(parsed)) {
      return new Set(parsed as string[]);
    }
  } catch {
    // Invalid or corrupted value; treat as no exclusions for report defaults.
  }
  return new Set();
}

function buildLabelFilterOptionsFromLabels(labels: ILabel[]): { id: string; name: string }[] {
  return labels.reduce((acc, l) => {
    const newOption = {
      id: l.includeInCounter ? l.uuid : l.categoryUuid,
      name: l.includeInCounter ? l.value : l.category,
    };
    const exists = acc.find((o) => o.id === newOption.id);
    if (!exists) acc.push(newOption);
    return acc;
  }, [] as { id: string; name: string }[]);
}

export interface IStep {
  id: string;
  methodTypeId: string;
  pipelineId: string;
  type: StepTask;
  assetSpecific: boolean;
  params: any[];
}
@Injectable()
export class ReportsEffects {
  private Case: typeof Parse.Object = Parse.Object.extend(CaseTopology.TABLE);
  private Sample: typeof Parse.Object = Parse.Object.extend(SampleTopology.TABLE);

  private MethodType: typeof Parse.Object = Parse.Object.extend(MethodTypeTopology.TABLE);

  private PipelineSteps: typeof Parse.Object = Parse.Object.extend(PipelineStepTopology.TABLE);

  constructor(
    private readonly actions$: Actions,
    private store$: Store,
    private readonly translateService: TranslateService,
    private mosaicService: MosaicService,
    private authService: AuthService,
    private featuresService: FeaturesService
  ) {}

  initializeReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.initializeReport),
      switchMap(({ caseId }) => {
        return of(ReportsActions.loadMethodTypes({ caseId }));
      })
    )
  );

  loadMethodTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.loadMethodTypes),
      switchMap(({ caseId }) =>
        from(this.getCaseMethodTypes(caseId)).pipe(
          map((methodTypes) => ReportsActions.methodTypesLoaded({ methodTypes, caseId })),
          catchError((error) =>
            of(
              ReportsActions.reportsError({
                error: `[loadMethodTypes]: ${error.message}`,
              })
            )
          )
        )
      )
    )
  );

  loadSamples$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.methodTypesLoaded),
      switchMap(({ caseId }) =>
        from(this.getSamples(caseId)).pipe(
          map((samples) =>
            ReportsActions.samplesLoaded({
              samples,
            })
          ),
          catchError((error) =>
            of(
              ReportsActions.reportsError({
                error: `[loadSamples]: ${error.message}`,
              })
            )
          )
        )
      )
    )
  );

  loadAnalysts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.samplesLoaded),
      switchMap(({ samples }) =>
        from(this.getSamplesAnalysts(samples)).pipe(
          map(({ analysts, analystIdsBySampleId }) =>
            ReportsActions.analystsLoaded({ analysts, analystIdsBySampleId })
          ),
          catchError((error) =>
            of(
              ReportsActions.reportsError({
                error: `[loadAnalysts]: ${error.message}`,
              })
            )
          )
        )
      )
    )
  );

  fetchInfoFromMethodTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.methodTypesLoaded),
      switchMap(({ methodTypes }) => {
        return methodTypes.map((methodType) => ReportsActions.getStepsForMethodType({ methodTypeId: methodType.id }));
      })
    )
  );

  getStepsForMethodType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.getStepsForMethodType),
      mergeMap(({ methodTypeId }) =>
        from(this.getStepsForMethodType(methodTypeId)).pipe(
          map((steps) => ReportsActions.stepsFromMethodTypeFetched({ steps, methodTypeId })),
          catchError((error) =>
            of(
              ReportsActions.reportsError({
                error: `[getStepsForMethodType]: ${error.message}`,
              })
            )
          )
        )
      )
    )
  );

  loadLabels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.stepsFromMethodTypeFetched),
      concatMap(({ steps }) => {
        const labelUuids = ReportMapper.getLabelUuidsFromSteps(steps);
        return from(this.fetchLabelsInBatches(labelUuids)).pipe(
          map((allLabels) => this.filterLabels(allLabels)),
          map((filteredLabels) => ReportMapper.toStateLabels(filteredLabels, steps)),
          map((labels) => ReportsActions.labelsLoaded({ labels })),
          catchError((error) =>
            of(
              ReportsActions.reportsError({
                error: `[loadLabels]: ${error.message}`,
              })
            )
          )
        );
      })
    )
  );

  loadInfoForSamples$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.loadInfoForSamples),
      mergeMap(({ sampleIds }) =>
        of(sampleIds).pipe(
          withLatestFrom(this.store$.select(getMethodTypeFromSample(sampleIds[0]))),
          map(([ids, methodTypeId]: [string[], string]) => ({
            sampleIds: ids,
            methodTypeId,
          }))
        )
      ),
      mergeMap(({ sampleIds, methodTypeId }) => {
        if (sampleIds?.length < 1) return EMPTY;
        return [
          ReportsActions.setSamplesForPreview({ sampleIds }),
          ReportsActions.setActiveMethodType({ methodTypeId }),
          ReportsActions.setLoading({ loading: true }),
          ...sampleIds.map((id) =>
            ReportsActions.loadInfoForSample({
              sampleId: id,
            })
          ),
        ];
      })
    )
  );

  loadInfoForSpecifiedSample$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.loadInfoForSample),
      mergeMap(({ sampleId }) => {
        return [
          ReportsActions.loadSampleAnalysis({ sampleId }),
          ReportsActions.loadSampleCounter({ sampleId }),
          ReportsActions.loadFavAssets({ sampleId }),
        ];
      })
    )
  );

  loadInitialAnalystFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.setActiveMethodType),
      withLatestFrom(this.store$.select(selectAnalysts)),
      switchMap(([{ methodTypeId }, analysts]) => {
        if (!methodTypeId) return EMPTY;
        const currentUserId = this.authService.currentUser?.id;
        const selfAnalyst = currentUserId ? analysts.find((an) => an.id === currentUserId) : undefined;
        const selectedOptions = selfAnalyst
          ? [{ id: selfAnalyst.id, name: selfAnalyst.name }]
          : analysts.map((an) => ({ id: an.id, name: an.name }));
        const newFilter = {
          filterKey: 'analyst',
          displayKey: 'analyst',
          selectedOptions,
          methodTypeId: methodTypeId,
        };
        if (selfAnalyst) {
          return from([
            ReportsActions.setupFilter({ filter: newFilter }),
            ReportsActions.cropAnalystChanged({ analyst: selfAnalyst }),
          ]);
        }
        return of(ReportsActions.setupFilter({ filter: newFilter }));
      })
    )
  );

  loadInitialLabelFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.setActiveMethodType),
      switchMap(({ methodTypeId }) =>
        this.store$.select(getLabelsByMethodTypeId(methodTypeId)).pipe(
          take(1),
          map((labels) => [methodTypeId, labels] as const)
        )
      ),
      mergeMap(([methodTypeId, labels]) => {
        if (!methodTypeId) return EMPTY;
        const allOptions = buildLabelFilterOptionsFromLabels(labels);
        const excludedFromCounter = readCounterExcludedLabelIdsFromStorage(methodTypeId);
        const selectedOptions = allOptions.filter((o) => !excludedFromCounter.has(o.id));
        const newFilter = {
          filterKey: 'label',
          displayKey: 'label',
          selectedOptions,
          methodTypeId,
        };
        return of(ReportsActions.setupFilter({ filter: newFilter }));
      })
    )
  );

  persistLabelFilterToCounterExcludedStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportsActions.setupFilter),
        filter((action) => action.filter.filterKey === 'label' && !!action.filter.methodTypeId),
        concatMap((action) =>
          this.store$.select(getLabelsByMethodTypeId(action.filter.methodTypeId)).pipe(
            take(1),
            tap((labels) => {
              if (!labels.length) {
                return;
              }
              const allOptions = buildLabelFilterOptionsFromLabels(labels);
              const allIds = allOptions.map((o) => o.id);
              const selected = new Set(action.filter.selectedOptions.map((o) => o.id));
              const excluded = allIds.filter((id) => !selected.has(id));
              localStorage.setItem(
                joinStorageKey(COUNTER_EXCLUDED_STORAGE_KEY_PREFIX, action.filter.methodTypeId),
                JSON.stringify(excluded)
              );
            })
          )
        )
      ),
    { dispatch: false }
  );

  loadInitialSampleAnalyses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.loadSampleAnalysis),
      withLatestFrom(this.store$.select(selectAnalysts)),
      switchMap(([{ sampleId }, analysts]) => {
        return analysts
          .filter((an) => an.type === 'user')
          .map((analyst) =>
            ReportsActions.loadSampleAnalystAnalysis({
              analystId: analyst.id,
              sampleId,
            })
          );
      })
    )
  );

  loadInitialSampleCounter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.loadSampleCounter),
      withLatestFrom(this.store$.select(selectAnalysts)),
      concatMap(([{ sampleId }, analysts]) => {
        const sample = this.Sample.createWithoutData(sampleId) as Sample;

        // `forkJoin([])` completes WITHOUT emitting, so with no analyst to count
        // for, the `setLoading(false)` below would never fire and the report
        // would sit on its loading template for good. An empty analyst list is
        // reachable whenever the licence scopes everyone out (see
        // `reportAnalystScope`).
        if (analysts.length === 0) {
          return of(ReportsActions.setLoading({ loading: false }));
        }

        return forkJoin(
          analysts.map((analyst) => {
            const createdType = analyst.type === 'user' ? User : Algorithms;
            const createdByObject = createdType.createWithoutData(analyst.id);

            return from(CloudFunctions.GetSampleAnalsysStats(sample, createdByObject.toPointer())).pipe(
              map((sampleStats) => ReportsEffects.toISampleCount(sampleId, analyst.id, sampleStats)),
              map((countInfo) => ReportsActions.counterForAnalystLoaded({ countInfo })),
              catchError((error) =>
                of(
                  ReportsActions.reportsError({
                    error: `[getSampleCounterForAnalyst]: ${error.message}`,
                  })
                )
              )
            );
          })
        ).pipe(switchMap((actions) => [...actions, ReportsActions.setLoading({ loading: false })]));
      })
    )
  );

  getAnalysisForAnalyst$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.loadSampleAnalystAnalysis),
      mergeMap(({ analystId, sampleId }) =>
        this.store$.select(getPipelinesForSample(sampleId)).pipe(
          filter((pipelineIds) => pipelineIds !== undefined && pipelineIds.length > 0),
          take(1),
          switchMap((pipelineIds) =>
            this.getSampleAnalyses(sampleId, analystId, pipelineIds).pipe(
              map((sampleAnalyses) =>
                ReportsActions.analysisForAnalystLoaded({
                  analyses: sampleAnalyses,
                })
              ),
              catchError((error) =>
                of(
                  ReportsActions.reportsError({
                    error: `[getAnalysisForAnalyst]: ${error.message}`,
                  })
                )
              )
            )
          )
        )
      )
    )
  );

  loadCrops$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.cropAnalystChanged),
      switchMap(({ analyst }) =>
        combineLatest([
          this.store$.select(getSampleCountersForAnalyst(analyst.id)),
          this.store$.select(selectLoading),
        ]).pipe(
          // The counters land asynchronously after cropAnalystChanged (e.g. the
          // auto-select on setActiveMethodType), so the first emission is often
          // undefined — but an analyst with no analysis on this sample never
          // gets a counter at all. Stop waiting once the counter load is done,
          // otherwise the crops spinner would never come down.
          filter(([countInfo, loadingCounters]) => countInfo != null || !loadingCounters),
          take(1),
          mergeMap(([countInfo]) =>
            this.store$.select(sampleCropsFromAnalyst(analyst.id)).pipe(
              take(1),
              mergeMap((analystCrops) => {
                if (!analyst || analystCrops?.length > 0) {
                  return of(ReportsActions.setLoadingCrops({ loading: false }));
                }
                if (!countInfo?.labelCount?.length) {
                  return of(ReportsActions.setLoadingCrops({ loading: false }));
                }

                const labelIds = countInfo.labelCount.map((lc) => lc.labelId);

                return from(labelIds).pipe(
                  mergeMap(
                    (labelId) => this.mosaicService.getRoisFromSample(countInfo.sampleId, analyst.id, 5, labelId),
                    5 // Máximo 5 peticiones simultáneas al servidor
                  ),
                  toArray(), // Espera a que todas terminen
                  map((allResults) => {
                    const cropInfo = allResults.flatMap((res) => res.items).map(ReportsEffects.toICropInfo);
                    return ReportsActions.cropInfoLoaded({ cropInfo });
                  }),
                  catchError((error) => of(ReportsActions.reportsError({ error: `[loadCrops]: ${error.message}` })))
                );
              })
            )
          )
        )
      )
    )
  );

  loadFavAssets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.loadFavAssets),
      mergeMap(({ sampleId }) => {
        const userId = this.authService.currentUser.id;
        return from(this.getFavAssets(userId, sampleId)).pipe(
          map((favAssetInfo) => ReportsActions.favAssetsLoaded({ favAssetInfo })),
          catchError((error) =>
            of(
              ReportsActions.reportsError({
                error: `[loadFavAssets]: ${error.message}`,
              })
            )
          )
        );
      })
    )
  );

  saveCropsInStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.cropInfoLoaded),
      mergeMap(({ cropInfo }) => {
        if (!cropInfo || cropInfo.length === 0) {
          return of(ReportsActions.setLoadingCrops({ loading: false }));
        }
        const cropBlobs$ = cropInfo.map((c) =>
          this.mosaicService.getCrop({ assetId: c.assetId, x0: c.x0, x1: c.x1, y0: c.y0, y1: c.y1 })
        );
        return forkJoin(cropBlobs$).pipe(
          mergeMap((cropBlobs) => {
            cropBlobs.map((blob, index) => {
              localStorage.setItem(`crop_${cropInfo[index].id}`, URL.createObjectURL(blob));
            });
            return of(ReportsActions.setLoadingCrops({ loading: false }));
          }),
          catchError((error) => {
            console.error('Error fetching or saving crop blob:', error);
            return of(
              ReportsActions.reportsError({
                error: null,
              }),
              ReportsActions.setLoadingCrops({ loading: false })
            );
          })
        );
      })
    )
  );

  clearStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.clearReportsState),
      tap(() => {
        Object.keys(localStorage)
          .filter((item) => item.startsWith('crop_'))
          .forEach((item) => localStorage.removeItem(item));
      }),
      map(() => {
        return { type: 'LocalStorageCleared' } as Action;
      })
    )
  );

  public static toSampleItem(sample) {
    return {
      id: sample.id,
      numImages: sample.get(SampleTopology.NUM_ASSETS),
      name: sample.get(SampleTopology.NAME),
      methodTypeId: sample.get(SampleTopology.METHOD_TYPE).id,
      active: false,
      visible: false,
      createdById: sample.get(SampleTopology.CREATED_BY)?.id,
      device: sample.get(SampleTopology.DEVICE)?.get(DeviceTopology.TYPE)?.get(DeviceTypeTopology.NAME),
      createdAt: new Date(sample.get(ObjectTopology.CREATED_AT)),
    };
  }

  public static toMethodTypeItem(methodType) {
    return {
      id: methodType.id,
      name: methodType.get(MethodTypeTopology.NAME),
      assetType: methodType.get(MethodTypeTopology.ASSET_TYPE).get(AssetTypeTopology.TYPE),
      active: false,
    };
  }

  public static toStepItem(step, methodTypeId): IStep {
    const params = step.get(PipelineStepTopology.PARAMS)?.categorization ?? [];
    return {
      id: step.id,
      methodTypeId: methodTypeId,
      pipelineId: step.get(PipelineStepTopology.PIPELINE).id,
      type: step.get(PipelineStepTopology.TASK),
      assetSpecific: step.get(PipelineStepTopology.GENERATEFINDING) === StepAction.ASSET_CREATED,
      params,
    };
  }

  public static toIAnalyst(analysisState): IAnalyst {
    const user = analysisState.get(AnalysisStateTopology.USER);
    const algorithm = analysisState.get(AnalysisStateTopology.ALGORITHM);
    const isAI = !!algorithm;
    return {
      id: isAI ? algorithm.id : user.id,
      name: isAI ? algorithm.name : user.username,
      type: isAI ? 'algorithm' : 'user',
    };
  }

  public static toISampleAnalysis(
    sampleId: string,
    analystId: string,
    content: any = {},
    params: any
  ): ISampleAnalysis {
    const info: ISampleAnalysisInfo = {};
    const { options, value } = content;

    (params || []).forEach(({ category, options: categoryOptions }) => {
      if (options) {
        const matchedOptions = categoryOptions.filter((option) => Object.keys(options).includes(option));
        if (matchedOptions.length > 0) {
          info[category] = matchedOptions;
        }
      } else if (value) {
        info[category] = [value];
      }
    });

    return {
      sampleId,
      analystId,
      info,
    };
  }

  public static toISampleCount(sampleId: string, analystId: string, stats: any = {}): ISampleCountEntity {
    return {
      sampleId,
      analystId,
      labelCount: stats.labelCount.map((l) => ({
        labelId: l.labelId,
        count: l.rois,
      })),
      totalCount: stats.labelCount.reduce((sum, lc) => sum + lc.rois, 0),
    };
  }

  public static toICropInfo(result) {
    const { creatorId, id, cropFile, sampleId, label, assetId, x0, x1, y0, y1 } = result;
    return {
      labelId: label,
      id,
      cropFile,
      sampleId,
      analystId: creatorId,
      assetId,
      x0,
      x1,
      y0,
      y1,
    };
  }

  public static toIFavAssetInfo(result, sampleId: string, analystId: string) {
    const assetFile = result.get(AssetTopology.ASSET_FILE);
    return {
      id: result.id,
      filename: assetFile,
      sampleId,
      analystId,
    };
  }

  private async getSamples(caseId: string) {
    const sampleQuery = new Parse.Query(SampleTopology.TABLE)
      .equalTo(SampleTopology.CASE, this.Case.createWithoutData(caseId))
      .include(`${SampleTopology.DEVICE}.${DeviceTopology.TYPE}`);

    const results = await sampleQuery.find();
    const samples = results.map(ReportsEffects.toSampleItem);

    return this.withAcquirerNames(samples);
  }

  /*
   * Resolved in its own query, deliberately: `createdBy` points at `_User`,
   * whose read is narrowed by hooks, and an include that failed would take the
   * whole sample list — and with it the entire report — down with it. A name we
   * cannot resolve just stays undefined and the report says "unknown".
   */
  private async withAcquirerNames(samples: ISample[]): Promise<ISample[]> {
    const creatorIds = [...new Set(samples.map((sample) => sample.createdById).filter((id): id is string => !!id))];
    if (creatorIds.length === 0) return samples;

    try {
      const users = await new Parse.Query(Parse.User).containedIn(ObjectTopology.ID, creatorIds).find();
      const namesById = new Map(users.map((user) => [user.id, user.get(UserTopology.USERNAME)]));

      return samples.map((sample) => ({ ...sample, createdByName: namesById.get(sample.createdById) }));
    } catch (error) {
      console.error('[getSamples] could not resolve the acquiring users:', error);
      return samples;
    }
  }

  private async getFavAssets(createdById: string, sampleId: string) {
    const analysisStateQuery = new Parse.Query(AnalysisStateTopology.TABLE)
      .equalTo(AnalysisStateTopology.SAMPLE, this.Sample.createWithoutData(sampleId))
      .equalTo(AnalysisStateTopology.USER, User.createWithoutData(createdById))
      .include(AnalysisStateTopology.FAVORITE_ASSETS);

    const result = await analysisStateQuery.first();

    if (!result) return [];

    const assets = result.get(AnalysisStateTopology.FAVORITE_ASSETS);

    return assets.map((a) => ReportsEffects.toIFavAssetInfo(a, sampleId, createdById));
  }

  /* Scoped by the organization's licence — see `reportAnalystScope`. */
  private async getSamplesAnalysts(
    samples: ISample[]
  ): Promise<{ analysts: IAnalyst[]; analystIdsBySampleId: Record<string, string[]> }> {
    const currentUserId = this.authService.currentUser?.id;

    /*
     * `FeaturesService.canUse` reads a licence the service caches from the
     * current organization, and it cannot tell "not licensed" from "not loaded
     * yet": both answer falsy. Reading it before the organization resolves
     * would scope the whole report down to nothing and leave the screen blank.
     * So while the licence is unknown, list everything — the scoping here is a
     * UX rule, the AnalysisState `beforeFind` on the server is the boundary.
     */
    const licenceKnown = !!this.authService.currentOrganizationValue?.license;
    const scope: ReportAnalystScope = licenceKnown
      ? reportAnalystScope({
          canReviewOthers: this.featuresService.canUse('reviewOthersAnalyses'),
          canUseAi: this.featuresService.canUse('ai'),
          currentUserId,
        })
      : { users: 'all', ai: true };

    if (isEmptyAnalystScope(scope)) return { analysts: [], analystIdsBySampleId: {} };

    const analysts: IAnalyst[] = [];
    const analystIdsBySampleId: Record<string, string[]> = {};

    for (const sample of samples) {
      const results = (await this.buildSampleAnalystsQuery(sample.id, scope, currentUserId).find()) ?? [];
      const sampleAnalysts = results.map(ReportsEffects.toIAnalyst);

      analysts.push(...sampleAnalysts);
      // Kept per sample as well: this is what tells "nobody analysed this
      // sample" apart from "an analyst went through it and found nothing".
      analystIdsBySampleId[sample.id] = [...new Set(sampleAnalysts.map((analyst) => analyst.id))];
    }

    return { analysts, analystIdsBySampleId };
  }

  private buildSampleAnalystsQuery(sampleId: string, scope: ReportAnalystScope, currentUserId?: string): Parse.Query {
    const forSample = () =>
      new Parse.Query(AnalysisStateTopology.TABLE).equalTo(
        AnalysisStateTopology.SAMPLE,
        this.Sample.createWithoutData(sampleId)
      );

    // Only analysts who actually produced something — see ANALYSED_STATES. Not
    // applied to the algorithm branch: AI states are not user progress.
    const forSampleWithAnalysis = () => forSample().containedIn(AnalysisStateTopology.STATE, ANALYSED_STATES);

    const withIncludes = (query: Parse.Query) =>
      query.include(AnalysisStateTopology.USER).include(AnalysisStateTopology.ALGORITHM);

    const queries: Parse.Query[] = [];
    if (scope.users === 'all') {
      queries.push(forSampleWithAnalysis().exists(AnalysisStateTopology.USER));
    } else if (scope.users === 'own') {
      queries.push(forSampleWithAnalysis().equalTo(AnalysisStateTopology.USER, User.createWithoutData(currentUserId)));
    }
    if (scope.ai) {
      queries.push(forSample().exists(AnalysisStateTopology.ALGORITHM));
    }

    return withIncludes(queries.length > 1 ? Parse.Query.or(...queries) : queries[0]);
  }

  private async getCaseMethodTypes(caseId: string) {
    const caseQuery = new Parse.Query(CaseTopology.TABLE).equalTo(ObjectTopology.ID, caseId);

    const caseObject = await caseQuery.first();

    const caseTypeQuery = new Parse.Query(CaseTypeTopology.TABLE).equalTo(
      ObjectTopology.ID,
      caseObject.get(`${CaseTopology.CASE_TYPE}`).id
    );

    const caseTypeObject = await caseTypeQuery.first();

    const methodTypesPointers = caseTypeObject.get(`${CaseTypeTopology.METHOD_TYPES}`);

    const methodTypeIds = methodTypesPointers.map((pointer: Parse.Object) => pointer.id);

    const methodTypesQuery = new Parse.Query(MethodTypeTopology.TABLE).containedIn(ObjectTopology.ID, methodTypeIds);

    const matchingMethodTypes = await methodTypesQuery.find();

    return matchingMethodTypes.map(ReportsEffects.toMethodTypeItem);
  }

  private async getStepsForMethodType(methodTypeId: string) {
    const protocolPipelineQuery = new Parse.Query(ProtocolPipelineTopology.TABLE).equalTo(
      ProtocolPipelineTopology.METHOD_TYPE,
      this.MethodType.createWithoutData(methodTypeId)
    );

    const pipelineQuery = new Parse.Query(PipelineTopology.TABLE).matchesKeyInQuery(
      ObjectTopology.ID,
      `${ProtocolPipelineTopology.PIPELINE}.${ObjectTopology.ID}`,
      protocolPipelineQuery
    );

    const pipelineStepsQuery = new Parse.Query(PipelineStepTopology.TABLE).matchesQuery(
      PipelineStepTopology.PIPELINE,
      pipelineQuery
    );

    const results = await pipelineStepsQuery.find();

    return results.map((step) => ReportsEffects.toStepItem(step, methodTypeId));
  }

  private getSampleAnalyses(sampleId: string, analystId: string, pipelineIds: string[]): Observable<ISampleAnalysis[]> {
    //Review duplicated code in analysis.service from analysis-refactor project
    const sample = this.Sample.createWithoutData(sampleId);
    const createdByObject = User.createWithoutData(analystId);

    const pipelinesQuery = new Parse.Query(PipelineTopology.TABLE).containedIn(ObjectTopology.ID, pipelineIds);

    const analysisQuery = new Parse.Query(AnalysisTopology.TABLE)
      .equalTo(AnalysisTopology.SAMPLE, sample)
      .equalTo(AnalysisTopology.CREATED_BY, createdByObject)
      .exists(AnalysisTopology.CREATED_BY)
      .matchesQuery(AnalysisTopology.PIPELINE, pipelinesQuery)
      .exists(AnalysisTopology.PIPELINE)
      .include([AnalysisTopology.PIPELINE])
      .descending(ObjectTopology.CREATED_AT)
      .doesNotExist(AnalysisTopology.ASSET);

    return from(
      analysisQuery
        .find()
        // Filter analysis with same analysis type, since they are ordered by creation, only the latest will be mapped
        .then((analysis) =>
          analysis.filter(
            (an, i, array) =>
              array.findIndex((v) => v.get(AnalysisTopology.PIPELINE).id === an.get(AnalysisTopology.PIPELINE).id) === i
          )
        )
        .then((analysis) => {
          const batchSize = 100;
          let skip = 0;
          const findingsFetched = [];

          const fetchNextBatch = (): Promise<any> => {
            const findingsQuery = new Parse.Query(FindingTopology.TABLE)
              .containedIn(FindingTopology.ANALYSIS, analysis)
              .equalTo(FindingTopology.CREATOR_ID, analystId)
              .include([FindingTopology.PIPELINE_STEP])
              .descending(FindingTopology.VERSION)
              .skip(skip)
              .limit(batchSize);

            return findingsQuery.find().then((findings) => {
              if (findings.length > 0) {
                findingsFetched.push(...findings);
                skip += batchSize;
                return fetchNextBatch();
              } else {
                return Promise.resolve({
                  findingsFetched,
                });
              }
            });
          };

          return fetchNextBatch().then(({ findingsFetched }) => {
            const lastUpdatedFindings = this.getHighestVersionFindings(findingsFetched);
            return lastUpdatedFindings.map((f) =>
              ReportsEffects.toISampleAnalysis(
                sampleId,
                analystId,
                f.get(FindingTopology.DATA)?.content[0],
                f.get(FindingTopology.PIPELINE_STEP)?.params?.categorization
              )
            );
          });
        })
    );
  }

  public async fetchLabelsInBatches(labelUuids, batchSize = 100) {
    let skip = 0;
    const allLabels = [];

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const labelsBatch = await new Parse.Query(LabelTopology.TABLE)
        .containedIn(LabelTopology.UUID, labelUuids)
        .limit(batchSize)
        .skip(skip)
        .ascending(ObjectTopology.CREATED_AT)
        .find();

      if (labelsBatch.length === 0) {
        break;
      }

      allLabels.push(...labelsBatch);

      skip += batchSize;
    }
    return allLabels;
  }

  public filterLabels(labels: Label[]) {
    const filteredLabelsMap = new Map();
    const lang = this.translateService.currentLang;

    labels.forEach((label) => {
      const existingLabel = filteredLabelsMap.get(label.uuid);

      if (!existingLabel) {
        filteredLabelsMap.set(label.uuid, label);
      } else if (label.lang === lang || (existingLabel.lang !== lang && label.lang === 'en')) {
        filteredLabelsMap.set(label.uuid, label);
      }
    });

    return Array.from(filteredLabelsMap.values());
  }

  public getHighestVersionFindings(findings: Finding[]) {
    //Review Duplicated function from analysis.service in analysis-refactor project
    const highestVersionMap = new Map();

    findings.forEach((finding) => {
      const analysisId = finding.analysis.id;
      const pipelineStepId = finding.pipelineStep.id;

      const key = `${analysisId}-${pipelineStepId}`;

      if (!highestVersionMap.has(key) || finding.version > highestVersionMap.get(key).version) {
        highestVersionMap.set(key, finding);
      }
    });

    const uniqueFindings = Array.from(highestVersionMap.values());

    return uniqueFindings;
  }
}
